import { computed, onUnmounted, unref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const isTwilioWhatsAppInbox = inbox => {
  if (!inbox) return false;
  const channelType = inbox.channelType || inbox.channel_type;
  const medium = inbox.medium || inbox.channel?.medium;
  return channelType === INBOX_TYPES.TWILIO && medium === 'whatsapp';
};

export const inboxSupportsTemplateSync = inbox => {
  if (!inbox) return false;

  const channelType = inbox.channelType || inbox.channel_type;
  if (
    channelType === INBOX_TYPES.WHATSAPP ||
    channelType === INBOX_TYPES.GUPSHUP
  ) {
    return true;
  }

  return isTwilioWhatsAppInbox(inbox);
};

const inboxHasCachedTemplates = (store, inboxId) => {
  if (store.getters['inboxes/getWhatsAppTemplates'](inboxId).length > 0) {
    return true;
  }

  const inbox = store.getters['inboxes/getInbox'](inboxId);
  const twilioTemplates = inbox?.content_templates?.templates;
  return Array.isArray(twilioTemplates) && twilioTemplates.length > 0;
};

const inboxHasFilteredTemplates = (store, inboxId) =>
  store.getters['inboxes/getFilteredWhatsAppTemplates'](inboxId).length > 0 ||
  store.getters['inboxes/getFilteredTwilioTemplates'](inboxId).length > 0;

/**
 * Loads WhatsApp templates: fetches inboxes from API (network), then syncs with provider if needed.
 */
export const useWhatsAppInboxTemplatesSync = (
  inboxIdRef,
  getInbox,
  { debounceMs = 300 } = {}
) => {
  const store = useStore();
  let loadTimer = null;

  const isSyncingTemplates = computed(
    () => store.getters['inboxes/getUIFlags']?.isSyncingTemplates ?? false
  );

  const loadTemplatesForInbox = async inboxId => {
    if (!inboxId) return;

    const inboxes = store.getters['inboxes/getInboxes'];
    const inbox = getInbox?.(inboxId);
    const shouldFetchInboxes =
      inboxes.length === 0 ||
      !inbox?.id ||
      !inboxHasCachedTemplates(store, inboxId);

    try {
      if (shouldFetchInboxes) {
        await store.dispatch('inboxes/fetchFromNetwork');
      }

      const refreshedInbox = getInbox?.(inboxId);
      if (!inboxSupportsTemplateSync(refreshedInbox)) return;

      if (!inboxHasFilteredTemplates(store, inboxId)) {
        await store.dispatch('inboxes/syncTemplates', inboxId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[WhatsApp templates] load failed', error);
    }
  };

  watch(
    () => unref(inboxIdRef),
    inboxId => {
      if (loadTimer) {
        clearTimeout(loadTimer);
        loadTimer = null;
      }

      if (!inboxId) return;

      loadTimer = setTimeout(() => {
        loadTimer = null;
        loadTemplatesForInbox(inboxId);
      }, debounceMs);
    },
    { immediate: true }
  );

  onUnmounted(() => {
    if (loadTimer) clearTimeout(loadTimer);
  });

  return {
    isSyncingTemplates,
    loadTemplatesForInbox,
    syncTemplatesForInbox: loadTemplatesForInbox,
  };
};

// Backward-compatible alias
export const useGupshupTemplatesSync = useWhatsAppInboxTemplatesSync;
